import React from "react";

import {
	Checkbox,
	FormField,
	FormRadioButtons,
	MultipleSelectInput,
	PanelFilters,
	SingleSelectInput,
	Text,
} from "@performyard/py-components";

import { SingleSelectEmployee } from "../../../../components/Inputs/SelectEmployee";

import { fetchEmployees } from "../../../../api/services/defs/employees";

import {
	type CompetenciesFilter,
	type CompetenciesFilterForm,
	CompetenciesFilterFormSchema,
	DEFAULT_STATUS,
	LEVEL_GAP_KEYS,
	LEVEL_GAP_LABELS,
	SOURCE_OPTIONS,
	STATUS_OPTIONS,
	type SelectFilterValue,
} from "./schema";

type CompetencyOption = { _id: string; name: string };
type RoleOption = { _id: string; name: string };

export type CompetenciesFilterPanelVariant = "my" | "team" | "company";

type CompetenciesFilterPanelProps = {
	isOpen: boolean;
	onClose: () => void;
	filters: CompetenciesFilter;
	onApplyFilters: (filters: CompetenciesFilter) => void;
	/** my = Type + Level Gap; team adds Assignee (direct reports) + Competency (single) + Role;
	 *  company adds Assignee (org-wide) + Manager + Competency (MULTI) + Role. */
	variant?: CompetenciesFilterPanelVariant;
	/** Team/company only — the Competency dropdown options. */
	competencyOptions?: CompetencyOption[];
	/** Team/company only — the Role dropdown options (the distinct own-roles in scope). */
	roleOptions?: RoleOption[];
};

export const CompetenciesFilterPanel = ({
	isOpen,
	onClose,
	filters,
	onApplyFilters,
	variant = "my",
	competencyOptions = [],
	roleOptions = [],
}: CompetenciesFilterPanelProps) => {
	const showPeopleFilters = variant !== "my";

	// Team scopes the assignee search to direct reports; company searches the whole org.
	const fetchAssigneeOptions = async (searchTerm: string): Promise<SelectFilterValue[]> => {
		const directReportsOnly = variant === "team";
		const employees = await fetchEmployees({ searchTerm, limitToDirectReports: directReportsOnly });
		return employees.users.map((user) => ({ id: user._id, name: user.name }));
	};

	// Manager is company-only — always searches the whole org.
	const fetchManagerOptions = async (searchTerm: string): Promise<SelectFilterValue[]> => {
		const employees = await fetchEmployees({ searchTerm, limitToDirectReports: false });
		return employees.users.map((user) => ({ id: user._id, name: user.name }));
	};

	const competencySelectOptions = competencyOptions.map((competency) => ({
		value: competency._id,
		label: competency.name,
	}));
	const roleSelectOptions = roleOptions.map((role) => ({ value: role._id, label: role.name }));

	const defaultValues: CompetenciesFilterForm = {
		status: filters.status ?? DEFAULT_STATUS,
		assignee: filters.assignee ?? null,
		manager: filters.manager ?? null,
		competency: filters.competency ?? null,
		competencies: filters.competencies ?? [],
		roles: filters.roles ?? [],
		source: filters.source ?? "any",
		exceeds: !!filters.levelGap?.includes("exceeds"),
		meets: !!filters.levelGap?.includes("meets"),
		below: !!filters.levelGap?.includes("below"),
		no_level: !!filters.levelGap?.includes("no_level"),
	};

	const handleSubmit = ({ value }: { value: CompetenciesFilterForm }) => {
		// Status is always applied (even at default) so its chip renders unconditionally.
		const applied: CompetenciesFilter = { status: value.status };
		if (showPeopleFilters) {
			if (value.assignee) applied.assignee = value.assignee;
			if (value.roles.length > 0) applied.roles = value.roles;
		}
		if (variant === "company" && value.manager) applied.manager = value.manager;
		if (variant === "team" && value.competency) applied.competency = value.competency;
		if (variant === "company" && value.competencies.length > 0) applied.competencies = value.competencies;
		if (value.source !== "any") applied.source = value.source;
		const levelGap = LEVEL_GAP_KEYS.filter((k) => value[k]);
		if (levelGap.length > 0) applied.levelGap = levelGap;
		onApplyFilters(applied);
		onClose();
	};

	return (
		<PanelFilters
			isOpen={isOpen}
			onClose={onClose}
			schema={CompetenciesFilterFormSchema}
			defaultValues={defaultValues}
			onSubmit={handleSubmit}
		>
			<div className="space-y-4">
				{showPeopleFilters && (
					<>
						{variant === "team" && (
							<FormField<SelectFilterValue | null>
								id="competency"
								label="Competency"
								input={(field) => (
									<SingleSelectInput<string>
										options={competencySelectOptions}
										selectionVariant="combobox"
										placeholder="Make a selection"
										isClearable
										value={field.value?.id}
										onChange={(id) => {
											const match = competencyOptions.find((option) => option._id === id);
											field.onChange(match ? { id: match._id, name: match.name } : null);
										}}
									/>
								)}
							/>
						)}
						{variant === "company" && (
							<FormField<SelectFilterValue[]>
								id="competencies"
								label="Competency"
								input={(field) => (
									<MultipleSelectInput<string>
										options={competencySelectOptions}
										selectionVariant="combobox"
										placeholder="Make a selection"
										value={(field.value ?? []).map((competency) => competency.id)}
										onChange={(competencyIds) =>
											field.onChange(
												competencyIds
													.map((competencyId) =>
														competencyOptions.find((option) => option._id === competencyId)
													)
													.filter((option): option is CompetencyOption => !!option)
													.map((option) => ({ id: option._id, name: option.name }))
											)
										}
									/>
								)}
							/>
						)}
					</>
				)}
				<FormRadioButtons<CompetenciesFilterForm["source"]> id="source" label="Type" options={SOURCE_OPTIONS} />
				{showPeopleFilters && (
					<>
						<FormField<SelectFilterValue | null>
							id="assignee"
							label="Assignee"
							input={(field) => (
								<SingleSelectEmployee
									placeholder="Make a selection"
									value={field.value ?? undefined}
									onChange={(employeeId, employeeName) =>
										field.onChange(
											employeeId && employeeName ? { id: employeeId, name: employeeName } : null
										)
									}
									fetchEmployees={fetchAssigneeOptions}
									data-testid="assignee-select"
								/>
							)}
						/>
						{variant === "company" && (
							<FormField<SelectFilterValue | null>
								id="manager"
								label="Manager"
								input={(field) => (
									<SingleSelectEmployee
										placeholder="Make a selection"
										value={field.value ?? undefined}
										onChange={(employeeId, employeeName) =>
											field.onChange(
												employeeId && employeeName
													? { id: employeeId, name: employeeName }
													: null
											)
										}
										fetchEmployees={fetchManagerOptions}
										data-testid="manager-select"
									/>
								)}
							/>
						)}
						<FormField<SelectFilterValue[]>
							id="roles"
							label="Role"
							input={(field) => (
								<MultipleSelectInput<string>
									options={roleSelectOptions}
									selectionVariant="combobox"
									placeholder="Make a selection"
									value={(field.value ?? []).map((role) => role.id)}
									onChange={(roleIds) =>
										field.onChange(
											roleIds
												.map((roleId) => roleOptions.find((option) => option._id === roleId))
												.filter((option): option is RoleOption => !!option)
												.map((option) => ({ id: option._id, name: option.name }))
										)
									}
								/>
							)}
						/>
					</>
				)}
				<FormRadioButtons<CompetenciesFilterForm["status"]>
					id="status"
					label="Assignment Status"
					options={STATUS_OPTIONS}
				/>
				<div className="space-y-2">
					<Text size="paragraph-3" weight="semi-bold">
						Level Gap
					</Text>
					{LEVEL_GAP_KEYS.map((key) => (
						<FormField<boolean>
							key={key}
							id={key}
							input={({ value, onChange }) => (
								<label htmlFor={key} className="flex cursor-pointer items-center gap-2">
									<Checkbox
										id={key}
										checked={value === true}
										onCheckedChange={(checked) => onChange(checked === true)}
										aria-label={LEVEL_GAP_LABELS[key]}
									/>
									<Text size="paragraph-3">{LEVEL_GAP_LABELS[key]}</Text>
								</label>
							)}
						/>
					))}
				</div>
			</div>
		</PanelFilters>
	);
};

export default CompetenciesFilterPanel;
